import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
	MultiSelect,
	MultiSelectContent,
	MultiSelectItem,
	MultiSelectTrigger,
} from "@/components/ui/MultiSelect";
import cacheKeys from "@/config/cache-keys";
import { capitalizeFirstLetter, removeExtension } from "@/lib/utils";
import AppService from "@/services/AppService";
import type { CreateLink } from "@/types/link/link";
import type { Group } from "@/types/group/group";
import { useQueryClient } from "@tanstack/react-query";
import {
	ArrowUpCircleIcon,
	CheckIcon,
	Link as LinkIcon,
	PenLineIcon,
	PlusIcon,
	TrashIcon,
	UsersIcon,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { toast } from "sonner";

const appService = new AppService();

interface UploadLinksButtonProps {
	groups: Group[];
}

export const UploadLinksButton = ({ groups }: UploadLinksButtonProps) => {
	const { t } = useTranslation();
	const queryClient = useQueryClient();

	// --- 2. State ---
	const [isOpen, setIsOpen] = useState(false);
	const [selectedGroups, setSelectedGroups] = useState<Set<string>>(new Set());
	const [selectedURLs, setSelectedURLs] = useState<CreateLink[]>([]);

	// --- 3. Derived data ---
	const { accountId } = useParams();
	const groupOptions = groups.map((group) => ({
		value: group.id,
		label: group.name,
	}));

	const handleUpload = async () => {
		if (!accountId) return toast.error("Ingen konto valgt.");
		if (selectedGroups.size === 0)
			return toast.error("Vennligst velg minst én gruppe.");
		if (selectedURLs.length === 0)
			return toast.error("Vennligst velg minst én fil.");
		if (selectedURLs.length > 10)
			return toast.error("Maks 10 filer kan lastes opp samtidig.");

		try {
			const promise = appService.crateLink(
				accountId,
				Array.from(selectedGroups),
				selectedURLs
			);

			toast.promise(promise, {
				loading: "Laster opp lenker...",
				success: "Lenker lastet opp!",
				error: "Noe gikk galt under opplasting av lenker.",
			});

			await promise;

			setSelectedGroups(new Set());
			setSelectedURLs([]);
			setIsOpen(false);
			queryClient.invalidateQueries({
				queryKey: cacheKeys.SINGLE_ACCOUNT(accountId),
			});
		} catch (error) {
			console.error("Error uploading links:", error);
		}
	};

	const addLink = () => {
		const newLink = {
			name: "",
			url: "",
		} as CreateLink;
		setSelectedURLs((prevLinks) => [...prevLinks, newLink]);
	};

	const removeLink = (index: number) => {
		setSelectedURLs((prevLinks) =>
			prevLinks.filter((_, linkIndex) => linkIndex !== index)
		);
	};

	const handleNameChange = (name: string, index: number) => {
		setSelectedURLs((links) =>
			links.map((link, linkIndex) =>
				linkIndex === index ? { ...link, name } : link
			)
		);
	};

	const handleUrlChange = (url: string, index: number) => {
		setSelectedURLs((links) =>
			links.map((link, linkIndex) =>
				linkIndex === index ? { ...link, url } : link
			)
		);
	};

	return (
		<Dialog open={isOpen} onOpenChange={setIsOpen}>
			<DialogTrigger asChild>
				<Button size="sm">
					<LinkIcon /> {t("admin.dialog.button.link-upload")}
				</Button>
			</DialogTrigger>
			<DialogContent className="sm:max-w-[750px] max-h-[90vh]">
				<DialogHeader>
					<DialogTitle>{t("admin.dialog.upload-links.link-title")}</DialogTitle>
					<DialogDescription>
						{t("admin.dialog.upload-links.link-content")}
					</DialogDescription>
				</DialogHeader>
				<div className="flex flex-col gap-4">
					<div className="grid grid-cols-2 gap-2">
						<div className="flex flex-col gap-2">
							<MultiSelect>
								<MultiSelectTrigger
									options={groupOptions}
									selectedValues={selectedGroups}
									name={t("admin.dialog.upload-links.group")}
									className="w-full justify-start"
									icon={<UsersIcon />}
								/>
								<MultiSelectContent
									clearSelected={() => setSelectedGroups(new Set())}
									selectedSize={selectedGroups.size}
									className="pointer-events-auto"
								>
									{groupOptions.map((option) => (
										<MultiSelectItem
											key={option.value}
											option={option}
											isSelected={selectedGroups.has(option.value)}
											toggleValue={() => {
												const newSelected = new Set(selectedGroups);
												if (newSelected.has(option.value)) {
													newSelected.delete(option.value);
												} else {
													newSelected.add(option.value);
												}
												setSelectedGroups(newSelected);
											}}
										/>
									))}
								</MultiSelectContent>
							</MultiSelect>
						</div>
					</div>
					{selectedURLs.length > 0 && (
						<div className="overflow-y-auto max-h-40 flex flex-col gap-2">
							{selectedURLs.map((link, index) => {
								return (
									<UploadedLinkCard
										link={link}
										onNameChange={(name) => handleNameChange(name, index)}
										onUrlChange={(url) => handleUrlChange(url, index)}
										onDelete={() => removeLink(index)}
									/>
								);
							})}
						</div>
					)}
					<Button
						variant="secondary"
						className="w-1/4 mx-auto"
						onClick={addLink}
					>
						<PlusIcon />
						{t("admin.dialog.upload-links.add-link")}
					</Button>
				</div>
				<DialogFooter>
					<Button type="submit" onClick={handleUpload}>
						<ArrowUpCircleIcon />
						{t("admin.dialog.button.link-upload")}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

interface UploadedLinkCardProps {
	link: CreateLink;
	onNameChange: (name: string) => void;
	onUrlChange: (url: string) => void;
	onDelete: () => void;
}

const UploadedLinkCard = ({
	link,
	onNameChange,
	onDelete,
	onUrlChange,
}: UploadedLinkCardProps) => {
	const { t } = useTranslation();

	const [linkName, setLinkName] = useState(link.name);
	const [linkUrl, setLinkUrl] = useState(link.url);
	const [isEditingName, setIsEditingName] = useState(false);
	const [isEditingUrl, setIsEditingUrl] = useState(false);

	const handleNameBlur = () => {
		setIsEditingName(false);
		const newName = capitalizeFirstLetter(removeExtension(linkName));
		setLinkName(newName);
		onNameChange(newName);
	};

	const handleLinkBlur = () => {
		setIsEditingUrl(false);
		let newUrl = linkUrl.trim();
		if (newUrl && !/^https?:\/\//i.test(newUrl)) {
			newUrl = "https://" + newUrl;
		}
		setLinkUrl(newUrl);
		onUrlChange(newUrl);
	};

	return (
		<Card className="p-4 flex items-center justify-between gap-2">
			<div className="flex items-center gap-2">
				<LinkIcon className="shrink-0 h-4 w-4" />
				{isEditingName ? (
					<form onSubmit={handleNameBlur} className="flex items-center gap-2">
						<Input
							value={linkName}
							onChange={(e) => setLinkName(e.target.value)}
							onBlur={handleNameBlur}
							autoFocus
						/>
						<Button size="sm" variant="secondary" onClick={handleNameBlur}>
							<CheckIcon className="h-4 w-4" />
						</Button>
					</form>
				) : (
					<div className="flex items-center gap-2 bg-soft-white dark:bg-[#121212] rounded-md">
						<Button
							variant="ghost"
							size="sm"
							onClick={() => setIsEditingName(true)}
						>
							{linkName || t("admin.dialog.upload-links.link-dnd.name-button")}
							<PenLineIcon className="h-4 w-4" />
						</Button>
					</div>
				)}
				{isEditingUrl ? (
					<div className="flex items-center gap-2">
						<Input
							value={linkUrl}
							onChange={(e) => setLinkUrl(e.target.value)}
							onBlur={handleLinkBlur}
							autoFocus
						/>
						<Button
							size="sm"
							variant="secondary"
							onClick={() => handleLinkBlur()}
						>
							<CheckIcon />
						</Button>
					</div>
				) : (
					<div className="flex items-center gap-2 bg-soft-white dark:bg-[#121212] rounded-md">
						<Button
							variant="ghost"
							size="sm"
							onClick={() => setIsEditingUrl(true)}
							className="w-full max-w-xs flex items-center justify-between"
						>
							<span className="truncate flex-grow text-left">
								{link.url || t("admin.dialog.upload-links.link-dnd.url-button")}
							</span>
							<PenLineIcon className="h-4 w-4 ml-2 shrink-0" />
						</Button>
					</div>
				)}
			</div>
			<div className="flex items-center gap-2">
				{!isEditingName && !isEditingUrl && (
					<Button size="sm" variant="destructive" onClick={onDelete}>
						<TrashIcon className="h-4 w-4" />
					</Button>
				)}
			</div>
		</Card>
	);
};
